/**
 * Habitat majority bonus scoring — §7.3.
 *
 * Bonus table:
 *
 * | playerCount | Unique largest | 2-way tie | 3+-way tie | Second |
 * |-------------|---------------|-----------|------------|--------|
 * | 1 (solo)    | +2 if size≥7  | —         | —          | —      |
 * | 2           | +2            | +1 each   | +1 each    | 0      |
 * | 3/4/5/6     | +3            | +2 each   | +1 each    | +1     |
 *
 * §9.6: 5–6 player fast-game reuses the 3/4-player table.
 *
 * Decision (spec §7.3 ambiguity): When ALL players have corridor size 0 for a
 * habitat, no bonuses are awarded — 0 is treated as "no corridor presence."
 *
 * Pure — no I/O, no mutations.
 */

import type { PlayerCount } from "../types/game-config";
import type { Habitat } from "../types/habitat";

export interface PlayerCorridorInput {
  readonly playerId: string;
  readonly largestCorridorByHabitat: Readonly<Record<string, number>>;
}

export interface MajorityResult {
  readonly playerId: string;
  readonly habitat: Habitat;
  readonly bonus: number;
}

/**
 * Computes majority bonuses for one habitat across all players.
 *
 * Returns one entry per player, always (bonus may be 0).
 */
export const scoreHabitatMajority = (
  players: readonly PlayerCorridorInput[],
  playerCount: PlayerCount,
  habitat: Habitat,
): readonly MajorityResult[] => {
  if (players.length === 0) {
    return [];
  }

  const sizes = players.map((p) => ({
    playerId: p.playerId,
    size: p.largestCorridorByHabitat[habitat] ?? 0,
  }));

  // Solo rule: +2 if corridor size ≥ 7, else 0
  if (playerCount === 1) {
    return sizes.map(({ playerId, size }) => ({
      playerId,
      habitat,
      bonus: size >= 7 ? 2 : 0,
    }));
  }

  // When everyone has 0, no bonuses
  const maxSize = Math.max(...sizes.map((s) => s.size));
  if (maxSize === 0) {
    return players.map((p) => ({ playerId: p.playerId, habitat, bonus: 0 }));
  }

  const useExtendedTable = playerCount >= 3; // 3, 4, 5, 6 all use 3/4-player table

  const firstPlacePlayers = sizes.filter((s) => s.size === maxSize);
  const tieCount = firstPlacePlayers.length;

  // Determine first-place bonus
  let firstBonus: number;
  if (tieCount === 1) {
    firstBonus = useExtendedTable ? 3 : 2;
  } else if (tieCount === 2) {
    firstBonus = useExtendedTable ? 2 : 1;
  } else {
    // 3+ way tie
    firstBonus = 1;
  }

  // Determine second-place bonus (only when first place is unique in 3/4-player)
  const secondSize =
    tieCount === 1
      ? Math.max(
          ...sizes.filter((s) => s.size !== maxSize).map((s) => s.size),
          -1,
        )
      : -1;

  const secondPlacePlayerIds =
    useExtendedTable && tieCount === 1 && secondSize > 0
      ? new Set(
          sizes.filter((s) => s.size === secondSize).map((s) => s.playerId),
        )
      : new Set<string>();

  const firstPlacePlayerIds = new Set(firstPlacePlayers.map((s) => s.playerId));

  return sizes.map(({ playerId }) => {
    if (firstPlacePlayerIds.has(playerId)) {
      return { playerId, habitat, bonus: firstBonus };
    }
    if (secondPlacePlayerIds.has(playerId)) {
      return { playerId, habitat, bonus: 1 };
    }
    return { playerId, habitat, bonus: 0 };
  });
};
